import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import { useWindowSize } from '@uidotdev/usehooks';
import { Button, Column, Container, Icon, Row } from '@pella/sash-framework';
import { Breadcrumbs, CTA, Image, Layout, LoadingOverlay, SEO } from '../../components';
import { isValidArray } from '../../components/utilities/validations';

import AuthorBio from '../../components/template/author/bio';
import LocalNavTop from '../../components/common/local-nav-top';
import AuthorCard from '../../components/template/author/author-card';

const Author = (props) => {
	const pageData = props.data.contentstackAuthor;
	const allAuthors = props.data.allAuthors.nodes;
	const allAuthorArticles = props.data.allAuthorArticles.nodes;
	const [articleCount, setArticleCount] = useState(3);
	const [loading, setLoading] = useState(false);
	const { width: windowWidth } = useWindowSize();
	const branchData = props.location.state?.branchData || pageData?.branch_data;
	const sdArticleArray = allAuthorArticles.slice(0, 8).map((article) => ({
		'@type': 'BlogPosting',
		'headline': article.top_content.heading,
		'url': process.env.GATSBY_SITE_URL + article.url,
		'datePublished': article.created_at,
	}));

	useEffect(() => {
		setArticleCount(windowWidth >= 1280 ? 8 : windowWidth >= 768 ? 6 : 3);
	}, [windowWidth]);

	const onLoadMoreClick = () => {
		setLoading(true);

		const countLimit = windowWidth >= 1280 ? 8 : windowWidth >= 768 ? 6 : 3;

		setTimeout(() => {
			setArticleCount(articleCount + countLimit);
			setLoading(false);
		}, 300);
	};

	return (
		<Layout
			id='template-Author'
			pageTitle={pageData.seo?.title}
		>
			{branchData?.[0] && (
				<LocalNavTop
					branchData={branchData[0]}
					linksArray={branchData[0].local_nav}
					isAnchors={false}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero_title && (
				<div className='bg-neutral-750 py-12 md:py-16'>
					<Container>
						<h1 className='text-32px font-semibold text-white xl:py-5 xl:text-[2.625rem]'>{pageData.hero_title}</h1>
					</Container>
				</div>
			)}

			{pageData.about?.heading && (
				<Container>
					<AuthorBio
						data={{
							...pageData.about,
							url: pageData.url,
							image: pageData.image,
							awards: pageData.awards,
							articles: sdArticleArray,
							branch: pageData.branch_data?.[0],
						}}
						isBranch={isValidArray(pageData.branch_data)}
						renderAs={!pageData.hero_title ? 'h1' : ''}
					/>
				</Container>
			)}

			{isValidArray(allAuthorArticles) && (
				<div className='relative bg-neutral-50 py-10'>
					{loading && <LoadingOverlay fixed={false} />}

					<Container>
						{pageData.see_more_from_author && <h2 className='text-subtitle mb-10 text-center'>{pageData.see_more_from_author}</h2>}

						<ul className='m-0 grid list-none grid-cols-1 gap-y-4 px-5 py-0 md:grid-cols-3 md:gap-x-3 md:px-0 xl:grid-cols-4 xl:gap-x-4'>
							{allAuthorArticles.slice(0, articleCount).map((article, index) => {
								const isBeforeAfter = article.modular_blocks?.find((block) => block.all_images != null)?.all_images?.image?.length > 1 ? true : false;

								return (
									<li key={index}>
										<Link
											to={article.url}
											className={`relative grid h-full grid-cols-1 grid-rows-2 items-center border-t-[6px] border-t-primary bg-white leading-snug no-underline transition-shadow hover:shadow-lg max-md:mx-auto max-md:max-w-[320px]`}
										>
											{article.modular_blocks?.find((block) => block.all_images != null)?.all_images?.image?.[0] && (
												<Image
													data={article.modular_blocks.find((block) => block.all_images != null).all_images.image[isBeforeAfter ? 1 : 0]}
													className='!block aspect-[1.4] w-full object-cover'
												/>
											)}

											<span className='flex flex-row items-center gap-x-2 p-4 pr-2 xl:p-6 xl:pr-4'>
												<span className='text-subtitle flex-1 font-bold'>{article.top_content.heading}</span>
												<Icon
													name='keyboard_arrow_right'
													className='flex-none text-3xl xl:text-4xl'
												/>
											</span>
										</Link>
									</li>
								);
							})}
						</ul>

						{allAuthorArticles.length > articleCount && (
							<div className='mt-7 px-5 text-center md:px-0'>
								<Button
									color='secondary'
									onClick={onLoadMoreClick}
								>
									Load More
								</Button>
							</div>
						)}
					</Container>
				</div>
			)}

			{isValidArray(pageData.more_authors?.ctas) && (
				<div className='py-12'>
					<Container>
						{/* {pageData.more_authors.heading && <h2 className='text-subtitle mb-4 text-center'>{pageData.more_authors.heading}</h2>} */}

						{/* <Row className='!-mx-2 [&>*]:!p-2'>
							{pageData.more_authors.other_bios.map((author, index) => {
								if (windowWidth < 1280 && index > 1) return;

								return (
									<Column
										key={index}
										xs={12}
										md={6}
										xl={4}
									>
										<AuthorCard
											url={author.url}
											name={author.about?.heading}
											title={author.about?.job_title}
											image={author.image?.[0]}
										/>
									</Column>
								);
							})}
						</Row> */}

						<div className='text-center'>
							<CTA data={pageData.more_authors.ctas[0]} />
						</div>
					</Container>
				</div>
			)}

			{pageData.url === '/authors/' && isValidArray(allAuthors) && (
				<div className='py-12'>
					<Container>
						<Row className='!-mx-2 [&>*]:!p-2'>
							{allAuthors
								.filter((author) => author.url != null && author.url !== '/authors/')
								.map((author, index) => (
									<Column
										key={index}
										xs={12}
										md={6}
										xl={4}
									>
										<AuthorCard
											url={author.url}
											name={author.about?.heading}
											title={author.about?.job_title}
											image={author.image?.[0]}
										/>
									</Column>
								))}
						</Row>
					</Container>
				</div>
			)}
		</Layout>
	);
};

Author.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Author;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackAuthor.seo} />;

export const query = graphql`
	query ($uid: String!, $authorName: String) {
		contentstackAuthor(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			branch_data {
				...BranchData
			}
			hero_title
			author_tag
			awards {
				url
				gatsbyImageData(quality: 90)
			}
			image {
				...Image
				image {
					url
					gatsbyImageData(quality: 90)
				}
			}
			about {
				heading
				job_title
				description
				expertise {
					heading
					subheading
				}
			}
			see_more_from_author
			more_authors {
				heading
				other_bios {
					...Author
				}
				ctas {
					...CTA
				}
			}
			seo {
				...SEO
			}
		}

		allAuthorArticles: allContentstackArticles(filter: { author_bio: { elemMatch: { author_tag: { eq: $authorName } } } }, sort: { created_at: DESC }) {
			nodes {
				...Article
			}
		}

		allAuthors: allContentstackAuthor(filter: { url: { ne: "/" }, about: { heading: { ne: "" } } }, sort: { about: { heading: ASC } }) {
			nodes {
				...Author
			}
		}
	}
`;
